import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { BASE_URL } from '../apiConfig'
import axios from 'axios';

const buildQueryParams = (params) => {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, value.toString());
    }
  }
  return searchParams.toString();
};

// ==================== ACH MIS ====================
export const fetchOpsMIS = createAsyncThunk(
  'fetchOpsMIS',
  async ({ type, jwtBearer }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${BASE_URL}mis/ops/get/?type=${type}`, {
        headers: {
          Authorization: `Bearer ${jwtBearer}`
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          window.location.reload();
        }
        const errorData = await response.json();
        throw new Error(errorData?.message || `HTTP error! Status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Fetch error:', error);
      return rejectWithValue(error.message);
    }
  }
);

// ==================== EXPECTED COLLECTION DUE ====================
export const fetchExpectedCollectionDue = createAsyncThunk(
  'fetchExpectedCollectionDue',
  async ({ type, jwtBearer, page = 1, limit = 10, ...searchParams }, { rejectWithValue }) => {
    try {
      const params = {
        page,
        limit,
        ...searchParams,
      };

      const url = `${BASE_URL}mis/ops/expected-collection-due/?${buildQueryParams(params)}`;

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${jwtBearer}`,
        },
      });

      return response.data;
    } catch (error) {
      if (error.response) {
        if (error.response.status === 401) {
          window.location.reload();
        }
        return rejectWithValue(error.response.data?.message || error.response.data?.detail || `HTTP error! Status: ${error.response.status}`);
      } else if (error.request) {
        return rejectWithValue('No response from server.');
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

// ==================== BOUNCE & CLEAR STATUS ====================
export const fetchBounceClearStatus = createAsyncThunk(
  'fetchBounceClearStatus',
  async ({ jwtBearer, page = 1, limit = 10, ...searchParams }, { rejectWithValue }) => {
    try {
      const params = {
        page,
        limit,
        ...searchParams,
      };

      const url = `${BASE_URL}mis/bounce-clear-status/?${buildQueryParams(params)}`;

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${jwtBearer}`,
        },
      });

      return response.data;
    } catch (error) {
      if (error.response) {
        if (error.response.status === 401) {
          window.location.reload();
        }
        return rejectWithValue(error.response.data?.message || error.response.data?.detail || `HTTP error! Status: ${error.response.status}`);
      } else if (error.request) {
        return rejectWithValue('No response from server.');
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

// ==================== UPLOAD BOUNCE & CLEAR ====================
export const uploadBounceClearStatus = createAsyncThunk(
  'uploadBounceClearStatus',
  async ({ jwt_token, uploadType, file, month }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('uploadType', uploadType);
      formData.append('month', month || new Date().toISOString().slice(0, 7));

      const response = await axios.post(
        `${BASE_URL}mis/bounce-clear-status/upload/`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${jwt_token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        if (error.response.status === 401) {
          window.location.reload();
        }
        return rejectWithValue(error.response.data?.message || error.response.data?.detail || `HTTP error! Status: ${error.response.status}`);
      } else if (error.request) {
        return rejectWithValue('No response from server.');
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

// ==================== DOWNLOAD BOUNCE & CLEAR ====================
export const downloadBounceClearStatus = createAsyncThunk(
  'downloadBounceClearStatus',
  async ({ jwtBearer }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${BASE_URL}mis/bounce-clear-status/download/`,
        {
          headers: {
            Authorization: `Bearer ${jwtBearer}`,
          },
          responseType: 'blob',
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        if (error.response.status === 401) {
          window.location.reload();
        }
        return rejectWithValue(error.response.data?.message || `HTTP error! Status: ${error.response.status}`);
      } else if (error.request) {
        return rejectWithValue('No response from server.');
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

// ==================== SLICE ====================
const opsMISSlice = createSlice({
  name: 'branchMIS',
  initialState: {
    // ===== ACH STATUS =====
    achData: null,
    achLoading: false,
    achError: null,

    // ===== UTR TRACKER =====
    utrData: null,
    utrLoading: false,
    utrError: null,

    // ===== DAILY STATUS =====
    dailyStatusData: null,
    dailyStatusLoading: false,
    dailyStatusError: null,

    // ===== DISBURSEMENT MIS =====
    disbMisData: null,
    disbMisLoading: false,
    disbMisError: null,

    // ===== EXPECTED COLLECTION DUE =====
    expectedCollectionLoading: false,
    expectedCollectionError: null,
    expectedCollectionData: [],
    latestCreatedAtDateTime: null,
    paginationDataExpectedCollectionData: {
      page: 1,
      limit: 10,
      total_pages: 0,
      total_count: 0,
    },

    // ===== BOUNCE & CLEAR STATUS =====
    bounceClearLoading: false,
    bounceClearError: null,
    bounceClearData: [],
    bounceClearLatestUpdated: null,
    // bounceClearSummary: {
    //   total: 0,
    //   bounced: 0,
    //   cleared: 0,
    //   pending: 0,
    // },
    paginationDataBounceClear: {
      page: 1,
      limit: 10,
      total_pages: 0,
      total_count: 0,
    },
    
    // ===== BOUNCE & CLEAR UPLOAD =====
    bounceClearUploadLoading: false,
    bounceClearUploadError: null,
    bounceClearUploadSuccess: false,
    bounceClearUploadData: null,

    // ===== BOUNCE & CLEAR DOWNLOAD =====
    bounceClearDownloadLoading: false,
    bounceClearDownloadError: null,
    bounceClearDownloadSuccess: false,
  },
  extraReducers: builder => {
    // ==========================================
    // ACH STATUS
    // ==========================================
    builder.addCase(fetchOpsMIS.pending, (state, action) => {
      const type = action.meta.arg?.type;
      if (type === 'ACH') {
        state.achLoading = true;
        state.achError = null;
        state.achData = null;
      } else if (type === 'UTR') {
        state.utrLoading = true;
        state.utrError = null;
        state.utrData = null;
      } else if (type === 'DAILY STATUS') {
        state.dailyStatusLoading = true;
        state.dailyStatusError = null;
        state.dailyStatusData = null;
      } else if (type === 'DISB MIS') {
        state.disbMisLoading = true;
        state.disbMisError = null;
        state.disbMisData = null;
      }
    })
    builder.addCase(fetchOpsMIS.fulfilled, (state, action) => {
      const type = action.meta.arg?.type;
      if (type === 'ACH') {
        state.achLoading = false;
        state.achData = action.payload;
        state.achError = null;
      } else if (type === 'UTR') {
        state.utrLoading = false;
        state.utrData = action.payload;
        state.utrError = null;
      } else if (type === 'DAILY STATUS') {
        state.dailyStatusLoading = false;
        state.dailyStatusData = action.payload;
        state.dailyStatusError = null;
      } else if (type === 'DISB MIS') {
        state.disbMisLoading = false;
        state.disbMisData = action.payload;
        state.disbMisError = null;
      }
    })
    builder.addCase(fetchOpsMIS.rejected, (state, action) => {
      const type = action.meta.arg?.type;
      if (type === 'ACH') {
        state.achLoading = false;
        state.achError = action.payload;
        state.achData = null;
      } else if (type === 'UTR') {
        state.utrLoading = false;
        state.utrError = action.payload;
        state.utrData = null;
      } else if (type === 'DAILY STATUS') {
        state.dailyStatusLoading = false;
        state.dailyStatusError = action.payload;
        state.dailyStatusData = null;
      } else if (type === 'DISB MIS') {
        state.disbMisLoading = false;
        state.disbMisError = action.payload;
        state.disbMisData = null;
      }
    })

    // ==========================================
    // EXPECTED COLLECTION DUE
    // ==========================================
    builder.addCase(fetchExpectedCollectionDue.pending, (state, action) => {
      state.expectedCollectionLoading = true
      state.expectedCollectionError = null
    })
    builder.addCase(fetchExpectedCollectionDue.fulfilled, (state, action) => {
      state.expectedCollectionLoading = false
      state.expectedCollectionData = action.payload.data.expected_collection_due || []
      state.latestCreatedAtDateTime = action.payload.data.latest_created_at
      
      state.paginationDataExpectedCollectionData = {
        page: action.payload.data.pagination.page,
        limit: action.payload.data.pagination.limit,
        total_pages: action.payload.data.pagination.total_pages,
        total_count: action.payload.data.pagination.total_count,
      };
      console.log("state.paginationDataExpectedCollectionData", state.paginationDataExpectedCollectionData)
    })
    builder.addCase(fetchExpectedCollectionDue.rejected, (state, action) => {
      console.log('Error', action.payload)
      state.expectedCollectionLoading = false
      state.expectedCollectionError = action.payload
    })

    // ==========================================
    // BOUNCE & CLEAR STATUS - FETCH
    // ==========================================
    builder.addCase(fetchBounceClearStatus.pending, (state, action) => {
      state.bounceClearLoading = true
      state.bounceClearError = null
    })
    builder.addCase(fetchBounceClearStatus.fulfilled, (state, action) => {
      state.bounceClearLoading = false
      state.bounceClearData = action.payload.data?.records || []
      state.bounceClearLatestUpdated = action.payload.data?.last_updated || null
      
      state.paginationDataBounceClear = {
        page: action.payload.data?.pagination?.page || 1,
        limit: action.payload.data?.pagination?.limit || 10,
        total_pages: action.payload.data?.pagination?.total_pages || 0,
        total_count: action.payload.data?.pagination?.total_count || 0,
      };
      
      if (action.payload.data?.summary) {
        state.bounceClearSummary = action.payload.data.summary;
      }
      
      console.log("Bounce Clear Data fetched:", state.bounceClearData.length, "records")
    })
    builder.addCase(fetchBounceClearStatus.rejected, (state, action) => {
      console.log('Bounce Clear Error:', action.payload)
      state.bounceClearLoading = false
      state.bounceClearError = action.payload
    })

    // ==========================================
    // BOUNCE & CLEAR - UPLOAD
    // ==========================================
    builder.addCase(uploadBounceClearStatus.pending, (state, action) => {
      state.bounceClearUploadLoading = true
      state.bounceClearUploadError = null
      state.bounceClearUploadSuccess = false
    })
    builder.addCase(uploadBounceClearStatus.fulfilled, (state, action) => {
      state.bounceClearUploadLoading = false
      state.bounceClearUploadSuccess = true
      state.bounceClearUploadData = action.payload
      console.log("Bounce Clear Upload Success:", action.payload)
    })
    builder.addCase(uploadBounceClearStatus.rejected, (state, action) => {
      console.log('Bounce Clear Upload Error:', action.payload)
      state.bounceClearUploadLoading = false
      state.bounceClearUploadError = action.payload
      state.bounceClearUploadSuccess = false
    })

    // ==========================================
    // BOUNCE & CLEAR - DOWNLOAD
    // ==========================================
    builder.addCase(downloadBounceClearStatus.pending, (state, action) => {
      state.bounceClearDownloadLoading = true
      state.bounceClearDownloadError = null
      state.bounceClearDownloadSuccess = false
    })
    builder.addCase(downloadBounceClearStatus.fulfilled, (state, action) => {
      state.bounceClearDownloadLoading = false
      state.bounceClearDownloadSuccess = true
      console.log("Bounce Clear Download Success")
    })
    builder.addCase(downloadBounceClearStatus.rejected, (state, action) => {
      console.log('Bounce Clear Download Error:', action.payload)
      state.bounceClearDownloadLoading = false
      state.bounceClearDownloadError = action.payload
      state.bounceClearDownloadSuccess = false
    })
  }
})

export default opsMISSlice.reducer